import { db } from "../../../../../firebaseConfig";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  writeBatch,
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

/**
 * Create a follow request
 * @param {string} fromUserId - Requester user ID
 * @param {string} toUserId - Target user ID
 * @returns {Promise<Object>} - Request result
 */
export const createFollowRequest = async (fromUserId, toUserId) => {
  try {
    if (!fromUserId || !toUserId) throw new Error("Missing user IDs");
    if (fromUserId === toUserId) throw new Error("Cannot follow yourself");

    const requestsRef = collection(db, "follow_requests");

    const existingQ = query(
      requestsRef,
      where("fromUserId", "==", fromUserId),
      where("toUserId", "==", toUserId),
      where("status", "==", "pending"),
    );

    const existingSnap = await getDocs(existingQ);
    if (!existingSnap.empty) {
      const docSnap = existingSnap.docs[0];
      return {
        id: docSnap.id,
        status: docSnap.data().status || "pending",
        existed: true,
      };
    }

    const docRef = await addDoc(requestsRef, {
      fromUserId,
      toUserId,
      status: "pending",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return { id: docRef.id, status: "pending", existed: false };
  } catch (e) {
    console.error("Error creating follow request:", e);
    throw e;
  }
};

/**
 * Get follow request between two users
 * @param {string} fromUserId - Requester user ID
 * @param {string} toUserId - Target user ID
 * @returns {Promise<Object|null>} - Follow request or null
 */
export const getFollowRequestBetweenUsers = async (fromUserId, toUserId) => {
  try {
    if (!fromUserId || !toUserId) return null;

    const requestsRef = collection(db, "follow_requests");
    const q = query(
      requestsRef,
      where("fromUserId", "==", fromUserId),
      where("toUserId", "==", toUserId),
    );

    const snap = await getDocs(q);
    if (snap.empty) return null;

    let latestDoc = snap.docs[0];
    for (const d of snap.docs) {
      const current = d.data();
      const latest = latestDoc.data();
      const currentTs = current.updatedAt || current.createdAt;
      const latestTs = latest.updatedAt || latest.createdAt;
      try {
        const currentMs = currentTs?.toMillis
          ? currentTs.toMillis()
          : currentTs
            ? new Date(currentTs).getTime()
            : 0;
        const latestMs = latestTs?.toMillis
          ? latestTs.toMillis()
          : latestTs
            ? new Date(latestTs).getTime()
            : 0;
        if (currentMs > latestMs) {
          latestDoc = d;
        }
      } catch {
        // If timestamp comparison fails, keep existing latestDoc
      }
    }

    return { id: latestDoc.id, ...latestDoc.data() };
  } catch (e) {
    console.error("Error fetching follow request between users:", e);
    throw e;
  }
};

/**
 * Cancel a follow request
 * @param {string} fromUserId - Requester user ID
 * @param {string} toUserId - Target user ID
 * @returns {Promise<Object>} - Cancel result
 */
export const cancelFollowRequest = async (fromUserId, toUserId) => {
  try {
    if (!fromUserId || !toUserId) throw new Error("Missing user IDs");

    const requestsRef = collection(db, "follow_requests");
    const q = query(
      requestsRef,
      where("fromUserId", "==", fromUserId),
      where("toUserId", "==", toUserId),
      where("status", "==", "pending"),
    );

    const snap = await getDocs(q);
    if (snap.empty) {
      return { success: false, error: "No pending request found" };
    }

    const batch = writeBatch(db);
    snap.docs.forEach((d) => {
      batch.delete(d.ref);
    });
    await batch.commit();

    return { success: true, deletedCount: snap.docs.length };
  } catch (e) {
    console.error("Error canceling follow request:", e);
    throw e;
  }
};

/**
 * Toggle user follow
 * @param {string} currentUserId - Current user ID
 * @param {string} targetUserId - Target user ID
 * @returns {Promise<Object>} - Follow status
 */
export const toggleUserFollow = async (currentUserId, targetUserId) => {
  try {
    if (!currentUserId || !targetUserId) throw new Error("Missing user IDs");
    if (currentUserId === targetUserId)
      throw new Error("Cannot follow yourself");

    const currentRef = doc(db, "users", currentUserId);
    const targetRef = doc(db, "users", targetUserId);

    const [currentSnap, targetSnap] = await Promise.all([
      getDoc(currentRef),
      getDoc(targetRef),
    ]);

    if (!targetSnap.exists()) throw new Error("Target user not found");
    if (!currentSnap.exists()) throw new Error("Current user not found");

    const currentData = currentSnap.data() || {};

    const isFollowing = Array.isArray(currentData.following)
      ? currentData.following.includes(targetUserId)
      : false;

    const batch = writeBatch(db);

    if (isFollowing) {
      batch.set(
        currentRef,
        {
          following: arrayRemove(targetUserId),
          followingCount: increment(-1),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      batch.set(
        targetRef,
        {
          followers: arrayRemove(currentUserId),
          followersCount: increment(-1),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    } else {
      batch.set(
        currentRef,
        {
          following: arrayUnion(targetUserId),
          followingCount: increment(1),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      batch.set(
        targetRef,
        {
          followers: arrayUnion(currentUserId),
          followersCount: increment(1),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
    }

    await batch.commit();

    return { isFollowing: !isFollowing };
  } catch (e) {
    console.error("Error toggling user follow:", e);
    throw e;
  }
};
